<template>
  <div
    class="relative bg-gradient-to-br from-gray-50 via-white to-logo-color/5 py-20 md:py-32 overflow-hidden"
  >
    <!-- Background Decorations -->
    <div class="absolute inset-0 pointer-events-none">
      <div
        class="absolute top-20 left-10 w-64 h-64 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute bottom-20 right-10 w-96 h-96 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <!-- Who We Are Section -->
      <div class="max-w-4xl mx-auto text-center mb-20" data-aos="fade-up">
        <span
          class="inline-flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full mb-6"
        >
          <span class="w-2 h-2 bg-logo-color rounded-full animate-pulse"></span>
          <span
            class="text-sm font-semibold text-logo-color uppercase tracking-wider"
            >Who We Are</span
          >
        </span>
        <h2
          class="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
        >
          Youth
          <span class="text-logo-color relative">
            Ally
            <svg
              class="absolute -bottom-2 left-0 w-full"
              height="8"
              viewBox="0 0 200 8"
              fill="none"
            >
              <path
                d="M1 5.5C47.6667 2.16667 141.4 -2.1 199 5.5"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                class="text-logo-color/30"
              />
            </svg>
          </span>
        </h2>
        <p class="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
          Youth Ally (YA) is a non-profit organisation committed to promoting
          ethical and transformative leadership among young people and equipping
          them with the skills to create dignified economic opportunities for
          themselves and their communities. YA is part of an ecosystem where
          young people hold the power to create practical solutions to unique
          problems in their communities.
        </p>
      </div>

      <!-- Vision & Mission Grid -->
      <div
        class="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto mb-20"
      >
        <!-- Vision Card -->
        <div
          class="bg-logo-color rounded-3xl p-8 text-white relative overflow-hidden shadow-[0_20px_60px_-10px_rgba(0,0,0,0.3)]"
          data-aos="fade-right"
        >
          <div
            class="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"
          ></div>
          <div
            class="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"
          ></div>

          <div class="relative z-10">
            <div
              class="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-7 w-7"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </div>
            <h3 class="text-2xl font-bold mb-4">Our Vision</h3>
            <p class="text-white/90 leading-relaxed text-lg">
              An inclusive and prosperous Africa, fostered by ethical,
              innovative, skilled and transformative youth leadership.
            </p>
          </div>
        </div>

        <!-- Mission Card -->
        <div
          class="bg-white rounded-3xl p-8 relative overflow-hidden shadow-[0_20px_60px_-10px_rgba(0,0,0,0.15)] border border-gray-100"
          data-aos="fade-left"
        >
          <div
            class="absolute top-0 right-0 w-40 h-40 bg-logo-color/5 rounded-full -translate-y-1/2 translate-x-1/2"
          ></div>

          <div class="relative z-10">
            <div
              class="w-14 h-14 bg-logo-color/10 rounded-2xl flex items-center justify-center mb-6"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-7 w-7 text-logo-color"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
            <p class="text-gray-600 leading-relaxed text-lg">
              To inspire and equip young people with the ethics, skills and
              transformative mindset to be self-reliant and agents of change
              through practical hands-on training, mentorship and coaching.
            </p>
          </div>
        </div>
      </div>

      <!-- Image and Additional Info -->
      <div
        class="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center"
      >
        <!-- Left side with image -->
        <div class="relative" data-aos="fade-right">
          <div class="relative max-w-lg mx-auto lg:mx-0">
            <div
              class="relative rounded-3xl overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)]"
            >
              <img
                :src="require('@/assets/about2.jpg')"
                alt="Youth Ally Team"
                class="w-full object-cover h-[400px] md:h-[450px]"
              />
              <div
                class="absolute inset-0 bg-gradient-to-t from-logo-color/70 via-transparent to-transparent"
              ></div>
              <div class="absolute bottom-0 left-0 right-0 p-6">
                <p class="text-white/80 text-sm font-medium">Since 2019</p>
                <h3 class="text-xl font-bold text-white">
                  Empowering Youth Across Africa
                </h3>
              </div>
            </div>
            <div
              class="absolute -bottom-6 -right-6 w-full h-full bg-logo-color/10 rounded-3xl -z-10 hidden lg:block"
            ></div>
          </div>
        </div>

        <!-- Right side - What We Do -->
        <div class="space-y-6" data-aos="fade-left">
          <span
            class="inline-flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 text-logo-color"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span
              class="text-sm font-semibold text-logo-color uppercase tracking-wider"
              >What We Do</span
            >
          </span>

          <h3 class="text-3xl font-bold text-gray-900">
            Creating Opportunities for
            <span class="text-logo-color">Young Leaders</span>
          </h3>

          <p class="text-gray-600 leading-relaxed">
            Youth Ally is a place where young people can transform their talents
            and ideas into thriving, competitive and ethical businesses that
            employ other young people and give them the same opportunity to
            grow.
          </p>

          <!-- Focus Areas -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
            <div
              class="flex items-start gap-3 p-4 bg-white rounded-xl shadow-md border border-gray-100"
            >
              <div
                class="w-10 h-10 bg-logo-color/10 rounded-lg flex items-center justify-center flex-shrink-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-logo-color"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <div>
                <h4 class="font-bold text-gray-900 text-sm">
                  Ethical Leadership
                </h4>
                <p class="text-gray-500 text-xs mt-1">
                  Building integrity-driven leaders
                </p>
              </div>
            </div>

            <div
              class="flex items-start gap-3 p-4 bg-white rounded-xl shadow-md border border-gray-100"
            >
              <div
                class="w-10 h-10 bg-logo-color/10 rounded-lg flex items-center justify-center flex-shrink-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-logo-color"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h4 class="font-bold text-gray-900 text-sm">Work Readiness</h4>
                <p class="text-gray-500 text-xs mt-1">
                  Preparing youth for careers
                </p>
              </div>
            </div>

            <div
              class="flex items-start gap-3 p-4 bg-white rounded-xl shadow-md border border-gray-100"
            >
              <div
                class="w-10 h-10 bg-logo-color/10 rounded-lg flex items-center justify-center flex-shrink-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-logo-color"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div>
                <h4 class="font-bold text-gray-900 text-sm">
                  Mentorship & Coaching
                </h4>
                <p class="text-gray-500 text-xs mt-1">
                  Guidance from industry experts
                </p>
              </div>
            </div>

            <div
              class="flex items-start gap-3 p-4 bg-white rounded-xl shadow-md border border-gray-100"
            >
              <div
                class="w-10 h-10 bg-logo-color/10 rounded-lg flex items-center justify-center flex-shrink-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-logo-color"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <div>
                <h4 class="font-bold text-gray-900 text-sm">
                  Entrepreneurship
                </h4>
                <p class="text-gray-500 text-xs mt-1">
                  Turning ideas into businesses
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DecadeExpi",
};
</script>

<style scoped>
/* Add any custom styles if needed */
</style>
